import { STATUTS_COMMANDE } from "@/utils/statut-commande";
import coursesExpressUtils from "@/utils/courses-express";
import { formatPrix } from "@/composables/text-transformation";

export const monCompteCommandeDetailProps = () => ({
  sections: {
    type: Array,
    required: false,
    default() {
      return [];
    }
  },
  magasin: {
    type: Object,
    required: false,
    default: null
  },
  commande: {
    type: Object,
    required: true
  },
  isPorteurCarteCora: {
    type: Boolean,
    required: false,
    default: false
  },
  isPorteurCarteMalin: {
    type: Boolean,
    required: false,
    default: false
  },
  isPro: {
    type: Boolean,
    required: false,
    default: false
  },
  isEligibleDefis: {
    type: Boolean,
    required: false,
    default: false
  }
});

export const monCompteCommandeDetailEmits = () => ([
  "setPopinAjoutCarteVisible"
]);

export const useMonCompteCommandeDetail = (props, context, renommerRef, genererFactureRef) => {

  const titre = ref(null);
  const urlFactureGeneree = ref(null);

  const nameChanged = (t) => {
    titre.value = t;
    renommerRef.value.hide();
  }
  const factureGeneree = (url) => {
    genererFactureRef.value.hide();
    urlFactureGeneree.value = url;
  }
  const setPopinAjoutCarte = () => {
    context.emit("setPopinAjoutCarteVisible");
  }

  const badgeVariant = computed(() => {
    switch (props.commande.attributes.statut_code) {
      case STATUTS_COMMANDE.commandee:
        return "warning";
      case STATUTS_COMMANDE.retiree:
        return "success";
      default:
        return "danger";
    }
  })
  const bySections = computed(() => {
    return coursesExpressUtils
      .listeBySections(
        props.commande.attributes.lignes_commande.data,
        props.sections
      )
      .map((item) => {
        return {
          libelle: item.libelle,
          valeur: item.valeur,
          articles: item.articles.map((produit) => {
            return {
              id: produit.id,
              designation: produit.attributes.designation,
              avantages_animco: produit.attributes.avantages_animco,
              prix: produit.attributes.prix_unitaire,
              prix_total_avec_remise:
                produit.attributes.prix_total_avec_remise,
              quantite: produit.attributes.quantite,
              visuel: produit.attributes.visuel,
              categorie_parente_id: produit.attributes.categorie_parente_id,
              suivi: produit.attributes.suivi
            };
          })
        };
      });
  })
  const nbProduits = computed(() => {
    return props.commande.attributes.lignes_commande.data.length;
  })
  const nomCommande = computed(() => {
    return titre.value || props.commande.attributes.titre;
  })
  const isArchive = computed(() => {
    return parseInt(props.commande.attributes.is_archive) === 1;
  })
  const showBonTicket = computed(() => {
    if (
      isArchive.value &&
      props.commande.attributes.statut_code === STATUTS_COMMANDE.commandee
    ) {
      return false;
    }
    return (
      props.commande.attributes.statut_code === STATUTS_COMMANDE.commandee ||
      props.commande.attributes.statut_code === STATUTS_COMMANDE.retiree
    );
  })
  const showGenererFacture = computed(() => {
    if (props.isPro || isArchive.value) {
      return false;
    }
    return (
      props.commande.attributes.statut_code === STATUTS_COMMANDE.retiree &&
      !props.commande.attributes.has_adresse_facture &&
      urlFactureGeneree.value === null
    );
  })
  const showImpressionFacture = computed(() => {
    if (props.isPro || isArchive.value) {
      return false;
    }
    return (
      props.commande.attributes.statut_code === STATUTS_COMMANDE.retiree &&
      (props.commande.attributes.has_adresse_facture ||
        urlFactureGeneree.value !== null)
    );
  })
  const urlFacture = computed(() => {
    return urlFactureGeneree.value || props.commande.attributes.url_facture;
  })
  const hasAvantages = computed(() => {
    return (
      props.commande.attributes.avantages_total_animco.points ||
      props.commande.attributes.avantages_total_animco.eurocora ||
      props.commande.attributes.avantages_total_animco.objets.length > 0
    );
  })
  const showNumeroBonCommandePro = computed(() => {
    return props.commande.attributes.statut_code === STATUTS_COMMANDE.retiree;
  })
  const hasManyModesPaiement = computed(() => {
    return (
      props.commande.attributes.modes_paiement &&
      props.commande.attributes.modes_paiement.length > 1
    );
  })
  const modePaiement = computed(() => {
    if (!hasManyModesPaiement.value) {
      return props.commande.attributes.mode_paiement;
    }
    return props.commande.attributes.modes_paiement.reduce(
      (acc, current, index) => {
        const prefix = index === 0 ? "" : ", ";
        return `${acc}${prefix}${current.libelle} (${formatPrix(current.valeur)})`;
      },
      ""
    );
  })
  const showAvantages = computed(() => {
    if (props.isPro) {
      return false;
    }
    return (
      props.commande.attributes.avantages_total_animco.points ||
      props.commande.attributes.avantages_total_animco.eurocora ||
      (props.commande.attributes.avantages_total_animco.objets &&
        props.commande.attributes.avantages_total_animco.objets.length > 0)
    );
  })
  const sousTotal = computed(() => {
    const remises = parseFloat(props.commande.attributes.montant_total_remise);
    return (
      parseFloat(props.commande.attributes.montant_sous_total_ttc) + remises
    );
  })
  const clickAndCollectTraiteurNoelService = computed(() => {
    if (!props.magasin) { return }
    const mdcClickAndCollect =
      props.magasin.attributes.modes_commande &&
      props.magasin.attributes.modes_commande.find(
        (e) => e.type === "CC_TRAITEUR_NOEL"
      );
    return mdcClickAndCollect ? mdcClickAndCollect.services[0] : {};
  })
  const clickAndCollectTraiteurService = computed(() => {
    if (!props.magasin) { return }
    const mdcClickAndCollect =
      props.magasin.attributes.modes_commande &&
      props.magasin.attributes.modes_commande.find(
        (e) => e.type === "CC_TRAITEUR_PERMANENT"
      );
    return mdcClickAndCollect ? mdcClickAndCollect.services[0] : {};
  })
  const clickAndCollectService = computed(() => {
    if (!props.magasin) { return }
    const mdcClickAndCollect =
      props.magasin.attributes.modes_commande &&
      props.magasin.attributes.modes_commande.find((e) => e.type === "CC");
    return mdcClickAndCollect ? mdcClickAndCollect.services[0] : {};
  })
  const isLDD = computed(() => {
    return props.commande.attributes.code_type_commande === "LDD";
  })

  return {
    titre,
    urlFactureGeneree,
    nameChanged,
    factureGeneree,
    setPopinAjoutCarte,
    badgeVariant,
    bySections,
    nbProduits,
    nomCommande,
    isArchive,
    showBonTicket,
    showGenererFacture,
    showImpressionFacture,
    urlFacture,
    hasAvantages,
    showNumeroBonCommandePro,
    hasManyModesPaiement,
    modePaiement,
    showAvantages,
    sousTotal,
    clickAndCollectTraiteurNoelService,
    clickAndCollectTraiteurService,
    clickAndCollectService,
    isLDD,
    STATUTS_COMMANDE

  }
}